<template>
    <div class="card p-shadow-app">
        <h1>Peças a Usar</h1>
        <Toast />

        <div class="p-fluid p-formgrid p-grid p-mb-3">
            <div class="p-field p-col-12 p-md-2">
                <label for="dateTo">Selecione a Data</label>
                <Calendar
                    id="dateTo"
                    v-model="filterDateTo"
                    dateFormat="dd/mm/yy"
                    placeholder="Data"
                    @input="fetchParts"
                />
            </div>
            <div class="p-field p-col-12 p-md-4">
                <label for="seeUsers" class="p-mb-2">Ver Técnicos ({{ Object.keys(selectedNodes).length }})</label>
                <div class="p-formgroup-inline">
                    <div class="p-field-checkbox">
                        <Checkbox
                            id="seeUsers"
                            inputId="seeUsers"
                            v-model="seeUsers"
                            binary
                        />
                        <label for="seeUsers">Mostrar Técnicos</label>
                    </div>
                </div>
            </div>
        </div>

        <div class="p-fluid p-grid p-mt-2">
            <div
                class="p-col-12 p-md-3"
                v-for="node in nodes"
                :key="node.key"
                v-show="seeUsers"
            >
                <Tree
                    selectionMode="checkbox"
                    :value="node"
                    :selectionKeys.sync="selectedNodes"
                    @node-select="onNodeSelect"
                    @node-unselect="onNodeUnselect">
                <template #default="slotProps">
                    <div
                    class="tree-border-color"
                    :style="{
                        'border-color':
                        slotProps.node.children != undefined
                            ? slotProps.node.children[0].officeColor
                            : slotProps.node.userColor,
                    }"
                    >
                    {{ slotProps.node.label }}
                    </div>
                </template>
                </Tree>
            </div>
        </div>

        <DataTable
            :value="list"
            :loading="loading"
            class="p-datatable-sm"
            :rowClass="rowClass"
            showGridlines
            responsiveLayout="scroll"
        >
            <template #empty>
                <div class="p-text-center p-p-3">
                    Nenhuma tarefa encontrada para esta data.
                </div>
            </template>

            <Column field="pat_number" header="PAT" :style="{ width: '80px' }">
                <template #body="slotProps">
                    <span class="clickable-item" v-tooltip="'Ver Pat'" @click="goToPat(slotProps.data.pat_number)">
                        <strong>{{ slotProps.data.pat_number }}</strong>
                    </span>
                </template>
            </Column>

            <Column field="technician" header="Técnico" :style="{ width: '150px' }">
                <template #body="slotProps">
                    <div>{{ slotProps.data.technician }}</div>
                    <small class="p-text-secondary">
                        {{ formatTime(slotProps.data.start) }} - {{ formatTime(slotProps.data.end) }}
                    </small>
                </template>
            </Column>

            <Column field="parts" header="Peças" :style="{ minWidth: '300px' }">
                <template #body="slotProps">
                    <div v-if="slotProps.data.parts && slotProps.data.parts.length > 0">
                        <div
                            v-for="part in slotProps.data.parts"
                            :key="part.id"
                            class="part-item p-mb-2"
                        >
                            <div class="p-d-flex p-ai-center p-mb-1">
                                <Badge
                                    :value="part.quantity"
                                    severity="primary"
                                    class="p-mr-2"
                                />
                                <strong>{{ part.ref?.trim() }}</strong>
                            </div>
                            <div class="p-text-secondary" style="font-size: 0.9rem;">
                                {{ part.design }}
                            </div>
                            <div v-if="part.destination_warehouse_id" class="p-text-secondary" style="font-size: 0.85rem;">
                                Armazém: {{ part.destination_warehouse_id }} - {{ part.destination_warehouse_name }}
                            </div>
                        </div>
                    </div>
                    <div v-else class="p-text-secondary">
                        <i class="pi pi-minus"></i> Sem peças
                    </div>
                </template>
            </Column>

            <Column field="pat_brand" header="Equipamento" :style="{ minWidth: '200px' }">
                <template #body="slotProps">
                    <div>
                        <strong>{{ slotProps.data.pat_brand?.trim() }}</strong>
                    </div>
                    <div>{{ slotProps.data.pat_model?.trim() }}</div>
                    <small class="p-text-secondary">SN: {{ slotProps.data.pat_serial_number }}</small>
                </template>
            </Column>

            <Column field="contact_name" header="Cliente" :style="{ minWidth: '200px' }">
                <template #body="slotProps">
                    <div>{{ slotProps.data.contact_name }}</div>
                    <small class="p-text-secondary">{{ slotProps.data.contact_phone }}</small>
                </template>
            </Column>

            <Column field="pat_status" header="Status" :style="{ width: '150px' }">
                <template #body="slotProps">
                    <span class="p-d-flex p-ai-center">
                        <span class="p-d-flex p-flex-column p-ml-2">
                            <span v-if="slotProps.data.pat_transport_document_code">
                            <b>Guia de Transporte:</b>
                            {{ slotProps.data.pat_transport_document_code }}
                            </span>
                            <span>
                            <b>Estado do PAT:</b>
                            <span class="p-badge p-component"
                            :style="{ backgroundColor: slotProps.data.color_status }">
                                {{ slotProps.data.pat_status }}
                            </span>
                            </span>
                            <span>
                            <b>Estado da Tarefa:</b>
                            {{
                                slotProps.data.status
                                .status_description
                            }}
                            </span>
                        </span>
                        </span>
                </template>
            </Column>

            <Column field="localization" header="Localização" :style="{ minWidth: '200px' }">
                <template #body="slotProps">
                    <div v-if="slotProps.data.localization">
                        <div>{{ slotProps.data.localization.address }}</div>
                        <small class="p-text-secondary">
                            {{ slotProps.data.localization.zip_code }}
                        </small>
                    </div>
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script>
import TechnicalManagementService from "@/services/technical-management.service";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Calendar from "primevue/calendar";
import Badge from "primevue/badge";

export default {
    name: "PartsToUse",
    components: {
        DataTable,
        Column,
        Calendar,
        Badge
    },
    data() {
        return {
            list: [],
            seeUsers: false,
            filterDateTo: null,
            nodes: [],
            selectedNodes: {},
            myUsers: [],
            loading: false
        };
    },
    async beforeMount() {
        this.dataToTree();
    },
    methods: {
        async fetchParts() {
            if (!this.filterDateTo) return;

            this.loading = true;

            let usersChecked = Object.keys(this.selectedNodes).filter(key => this.selectedNodes[key].checked);

            try {
                const response = await TechnicalManagementService.getPartsToUse(
                    this.filterDateTo,
                    usersChecked
                );
                this.list = response || [];
            } catch (error) {
                this.$toast.add({
                    severity: "error",
                    summary: "Erro",
                    detail: "Erro ao buscar peças a usar.",
                    life: 3000,
                });
                this.list = [];
            } finally {
                this.loading = false;
            }
        },
        formatTime(date) {
            return new Date(date).toLocaleTimeString('pt-PT', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        },
        rowClass(data) {
            if (data.parts && data.parts.length > 0) {
                return 'task-with-parts';
            }
            return '';
        },
        goToPat(patId) {
            this.$router.push(`/pats/${patId}`);
        },
        dataToTree() {
            let hasLocal = false;
            if (localStorage.selectedNodes != undefined) {
                hasLocal = true;
                this.selectedNodes = JSON.parse(localStorage.selectedNodes);
            }
            for (const [office, user] of Object.entries(
                this.$store.state.auth.user.supportUsers
            )) {
                let users = [];
                user.forEach((myUser) => {
                    this.myUsers.push({
                        id: myUser.id,
                        username: myUser.username,
                        office: office,
                        userColor: myUser.userColor,
                        officeColor: myUser.officeColor,
                        technicianInitials: myUser.technicianInitials,
                    });
                    users.push({
                        key: myUser.id,
                        label: myUser.username,
                        data: `${office} Office User`,
                        icon: "pi pi-fw pi-user",
                        userColor: myUser.userColor,
                        officeColor: myUser.officeColor,
                        technicianInitials: myUser.technicianInitials,
                    });

                    if (!hasLocal) {
                        this.selectedNodes[myUser.id] = {
                        checked: true,
                        partialChecked: false,
                        };
                    }
                });
                let tree = {
                key: office,
                label: office,
                data: `office`,
                icon: "pi pi-fw pi-home",
                children: users,
                };
                this.nodes.push({ tree });
                if (!hasLocal) {
                this.selectedNodes[office] = {
                    checked: true,
                    partialChecked: false,
                };
                }
            }
            if (!hasLocal) {
                localStorage.selectedNodes = JSON.stringify(this.selectedNodes);
            }
        },
        async onNodeSelect() {
            localStorage.selectedNodes = JSON.stringify(this.selectedNodes);
            await this.fetchParts();
        },
        async onNodeUnselect() {
            localStorage.selectedNodes = JSON.stringify(this.selectedNodes);
            await this.fetchParts();
        },
    },
    mounted() {
        this.filterDateTo = new Date();
        this.fetchParts();
    },
};
</script>

<style scoped>
.part-item {
    padding: 8px;
    background-color: #f8f9fa;
    border-radius: 4px;
    border-left: 3px solid #2196F3;
}

.part-item:last-child {
    margin-bottom: 0 !important;
}

.summary-card {
    text-align: center;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 6px;
}

.summary-label {
    font-size: 0.9rem;
    color: #6c757d;
    margin-bottom: 0.5rem;
}

.summary-value {
    font-size: 2rem;
    font-weight: bold;
    color: #2196F3;
}

.task-with-parts {
    background-color: #f0f9ff !important;
}

::v-deep .p-datatable .p-datatable-tbody > tr > td {
    padding: 1rem;
}

.p-tree .p-tree-container .p-treenode .p-treenode-content.p-highlight {
  background: #fff;
  color: #000;
}

.p-tree-container .pi-check:before {
  color: black;
}
.p-tree-container .pi-user:before {
  color: #6c757d;
}
.p-tree-container .pi-home:before {
  color: #6c757d;
}
.p-tree-container .pi-chevron-right:before {
  color: #6c757d;
}

</style>